import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const PpcVsPfrSection: React.FC = () => {
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const scrollToSection = (href: string) => {
        if (href.startsWith('/')) {
            window.location.href = href;
        } else {
            const element = document.querySelector(href);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    return (
        <section id="ppc-vs-pfr" className="py-20 bg-white relative overflow-hidden">
            <div className="container mx-auto px-5 relative z-10">
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: 30 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl md:text-5xl font-bold mb-6 text-blue-900">
                        PPC vs. PFR: Which Drives Real Growth?
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                        In the digital age, how you pay for marketing matters as much as where you market.
                        Understanding the difference between paying for <strong>effort</strong> and paying for <strong>results</strong> is the key to scaling efficiently.
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-2 gap-12 items-start mb-16">
                    {/* PPC Column */}
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={inView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="bg-red-50 p-8 rounded-3xl border border-red-100 shadow-sm"
                    >
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center text-3xl shadow-lg">
                                💸
                            </div>
                            <h3 className="text-3xl font-bold text-red-900">PPC (Pay-Per-Click)</h3>
                        </div>
                        <p className="text-gray-700 mb-6 text-lg">
                            The traditional model where you pay every time someone clicks your ad, regardless of whether they buy or even qualify as a lead.
                        </p>
                        <ul className="space-y-4">
                            <li className="flex items-start gap-3">
                                <span className="text-red-500 font-bold">✕</span>
                                <span className="text-gray-700"><strong>High Risk:</strong> You pay for curiosity, not commitment.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-red-500 font-bold">✕</span>
                                <span className="text-gray-700"><strong>Budget Drain:</strong> Fraudulent clicks and "tire-kickers" eat your ROI.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-red-500 font-bold">✕</span>
                                <span className="text-gray-700"><strong>Unpredictable:</strong> Costs rise with competition, even if quality stays low.</span>
                            </li>
                        </ul>
                    </motion.div>

                    {/* PFR Column */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={inView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="bg-green-50 p-8 rounded-3xl border border-green-100 shadow-md relative"
                    >
                        <div className="absolute -top-4 -right-4 bg-yellow-400 text-blue-900 px-4 py-1 rounded-full font-bold text-sm shadow-md animate-pulse">
                            MOST EFFICIENT
                        </div>
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center text-3xl shadow-lg">
                                🎯
                            </div>
                            <h3 className="text-3xl font-bold text-green-900">PFR (Pay-For-Result)</h3>
                        </div>
                        <p className="text-gray-700 mb-6 text-lg">
                            The modern, performance-driven model where your investment is tied directly to qualified leads and verified sales.
                        </p>
                        <ul className="space-y-4">
                            <li className="flex items-start gap-3">
                                <span className="text-green-600 font-bold">✓</span>
                                <span className="text-gray-700"><strong>Zero Waste:</strong> Only pay when a real, qualified business outcome occurs.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-green-600 font-bold">✓</span>
                                <span className="text-gray-700"><strong>Aligned Incentives:</strong> We grow only when <em>you</em> grow.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-green-600 font-bold">✓</span>
                                <span className="text-gray-700"><strong>Scalable ROI:</strong> Every dollar spent has a measurable impact on your bottom line.</span>
                            </li>
                        </ul>
                    </motion.div>
                </div>

                {/* Affiliate Content Callout */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-3xl p-10 md:p-12 shadow-2xl relative overflow-hidden"
                >
                    {/* Decorative Elements */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-400 opacity-10 rounded-full translate-y-1/2 -translate-x-1/2"></div>

                    <div className="relative z-10 text-center">
                        <h3 className="text-3xl md:text-4xl font-bold mb-6">
                            Incorporate an Automated PFR Engine Into Your Website
                        </h3>
                        <p className="text-xl text-blue-100 mb-8 max-w-4xl mx-auto">
                            We've built and battle-tested a custom, branded affiliate marketing system right here.
                            Now, you can deploy the same high-performance system for your business to transform your growth into a result-driven machine.
                        </p>

                        <div className="flex flex-col md:flex-row items-center justify-center gap-6">
                            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 text-left">
                                <div className="text-3xl font-bold mb-1">$5,000</div>
                                <div className="text-blue-200">Total Implementation & Deployment</div>
                            </div>

                            <motion.button
                                className="bg-white text-blue-700 px-10 py-5 rounded-2xl font-bold text-xl hover:bg-blue-50 transition-all shadow-xl"
                                onClick={() => scrollToSection('/pay-for-result')}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                Learn More About PFR System →
                            </motion.button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default PpcVsPfrSection;
